import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "../../components/ui/sheet";
import { cn } from "../../lib/utils";
import { ChevronUp, ChevronDown, X } from "lucide-react";
import { Button } from "../../components/ui/button";

export interface AdminFlyoutPaneProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  width?: "sm" | "md" | "lg" | "xl" | "2xl" | "half" | "full";
  mode?: "view" | "edit" | "create";
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  headerActions?: React.ReactNode;
}

const widthClasses = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
  "2xl": "sm:max-w-4xl",
  half: "sm:max-w-[50vw]",
  full: "sm:max-w-full",
};

export function AdminFlyoutPane({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = "lg",
  mode = "view",
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
  headerActions,
}: AdminFlyoutPaneProps) {
  const showNavigation = onNavigatePrevious || onNavigateNext;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn("w-full overflow-y-auto", widthClasses[width])}
        side="right"
      >
        <SheetHeader className="mb-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-2xl truncate">{title}</SheetTitle>
              <SheetDescription className="text-base truncate">
                {description || "Details"}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {headerActions}
              {showNavigation && (
                <>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={onNavigatePrevious}
                    disabled={!hasPrevious}
                    title="Previous user (↑)"
                  >
                    <ChevronUp className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={onNavigateNext}
                    disabled={!hasNext}
                    title="Next user (↓)"
                  >
                    <ChevronDown className="h-5 w-5" />
                  </Button>
                </>
              )}
              <SheetClose asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 ml-4"
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </Button>
              </SheetClose>
            </div>
          </div>
        </SheetHeader>
        <div className="space-y-6">{children}</div>
      </SheetContent>
    </Sheet>
  );
}

// Reusable section components for consistent layout
export function FlyoutSection({
  title,
  children,
  className,
}: {
  title?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {title && (
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
          {title}
        </h3>
      )}
      <div className="space-y-2">{children}</div>
    </div>
  );
}

export function FlyoutField({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("space-y-1", className)}>
      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {label}
      </label>
      <div className="text-sm text-gray-900 dark:text-white">{value}</div>
    </div>
  );
}

export function FlyoutActions({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center gap-2 pt-4 border-t", className)}>
      {children}
    </div>
  );
}
